import { useState, useEffect } from 'react';

const STORAGE_KEY = 'neonpharma_article_views';
const SESSION_KEY = 'neonpharma_viewed_slugs';

interface ViewsMap {
  [slug: string]: number;
}

const getViewsMap = (): ViewsMap => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as ViewsMap) : {};
  } catch {
    return {};
  }
};

const getSessionViewed = (): string[] => {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    return raw ? (JSON.parse(raw) as string[]) : [];
  } catch {
    return [];
  }
};

const saveViewsMap = (map: ViewsMap): void => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(map));
  } catch {
    // ignore quota errors
  }
};

const saveSessionViewed = (slugs: string[]): void => {
  try {
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(slugs));
  } catch {
    // ignore
  }
};

/**
 * Tracks article view counts in localStorage.
 * Each unique slug is counted once per browser session to avoid inflation on refresh.
 * Returns the current view count for the given slug.
 */
export const useArticleViews = (slug: string | undefined): number => {
  const [views, setViews] = useState<number>(0);

  useEffect(() => {
    if (!slug) return;

    const map = getViewsMap();
    const sessionViewed = getSessionViewed();

    // Increment only if not already viewed in this session
    if (!sessionViewed.includes(slug)) {
      const updated: ViewsMap = { ...map, [slug]: (map[slug] ?? 0) + 1 };
      saveViewsMap(updated);
      saveSessionViewed([...sessionViewed, slug]);
      setViews(updated[slug]);
    } else {
      setViews(map[slug] ?? 1);
    }
  }, [slug]);

  return views;
};

/**
 * Returns view counts for all articles (for use on blog listing page).
 */
export const getAllArticleViews = (): ViewsMap => getViewsMap();

/**
 * Returns formatted view count string: 1 234
 */
export const formatViews = (count: number): string =>
  count.toLocaleString('ru-RU');
